import { useCurrentPageAccess } from "@/app/router";
import { DateRangePicker } from "@/components/DateRangePicker";
import { ConfirmDialog } from "@/shared/components/feedback/ConfirmDialog";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useDialog } from "@/shared/hooks/useDialog";
import { toast } from "@/shared/hooks/useToast";
import type { DateRange } from "@/shared/types";
import { format, parseISO } from "date-fns";
import { useMemo } from "react";
import { S3KeyList } from "../components/S3KeyList";
import { useAppsflyerRawDelete } from "../hooks/useAppsflyerRawDelete";
import type { AppsflyerReportType } from "../types/recover-types";

export function RecoverAppsflyerRawFileDeletePage() {
  const vm = useAppsflyerRawDelete();
  const confirm = useDialog<void>();
  const { canWriteCurrentPage } = useCurrentPageAccess();
  const range: DateRange = useMemo(
    () => ({
      from: vm.params.startDate ? parseISO(vm.params.startDate) : undefined,
      to: vm.params.endDate ? parseISO(vm.params.endDate) : undefined,
    }),
    [vm.params.startDate, vm.params.endDate]
  );

  return (
    <div className="space-y-6">
      <Breadcrumbs />
      <PageHeader
        title="AppsFlyer Raw 파일 삭제"
        description="S3에 저장된 AppsFlyer Raw 데이터 파일들을 계정별, 기간별로 선택하여 삭제할 수 있습니다."
      />

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <div className="text-sm font-medium">dw_account_id</div>
            <Input
              value={vm.params.dwAccountId}
              onChange={(e) => vm.setParams((p) => ({ ...p, dwAccountId: e.target.value }))}
              className="w-[220px]"
              maxLength={256}
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">조회 기간</div>
            <DateRangePicker
              value={range}
              onChange={(next) =>
                vm.setParams((p) => ({
                  ...p,
                  startDate: next.from ? format(next.from, "yyyy-MM-dd") : "",
                  endDate: next.to ? format(next.to, "yyyy-MM-dd") : "",
                }))
              }
            />
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">리포트 타입</div>
            <Select
              value={vm.params.reportType}
              onValueChange={(v) =>
                vm.setParams((p) => ({ ...p, reportType: v as AppsflyerReportType }))
              }
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {vm.reportTypeOptions.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={async () => {
              const res = await vm.findKeys();
              if (!res.ok) toast({ title: res.error, variant: "destructive" });
            }}
            disabled={vm.isLoading}
          >
            조회
          </Button>

          <Button
            variant="destructive"
            onClick={() => confirm.open()}
            disabled={!vm.canDelete || vm.isLoading || !canWriteCurrentPage}
          >
            삭제
          </Button>
        </div>
      </Card>

      <S3KeyList keys={vm.s3KeysToDelete} />

      <ConfirmDialog
        open={confirm.isOpen}
        onOpenChange={(open) => !open && confirm.close()}
        title="삭제 확인"
        description={`총 ${vm.s3KeysToDelete.length}개 파일을 정말로 삭제하시겠습니까?`}
        confirmText="삭제"
        variant="destructive"
        onConfirm={async () => {
          const res = await vm.deleteKeys();
          if (!res.ok) {
            toast({ title: res.error, variant: "destructive" });
            return;
          }
          toast({ title: "삭제를 완료했습니다." });
          confirm.close();
        }}
        isLoading={vm.isLoading}
      />
    </div>
  );
}
